import { FormEvent, useEffect, useState } from 'react';
import {
  FormField,
  FormTextArea,
  FormTextInput,
  PrimaryButton,
  SecondaryButton,
  StandardConfirm,
  StandardModal,
} from './base';
import { cameraGroupsApi } from '@api/camera-groups';
import { HttpError } from '@api/http';
import type { CameraGroup } from '@api/types';

interface Props {
  open: boolean;
  groups: CameraGroup[];
  groupsLoading: boolean;
  onClose: () => void;
  onChanged: () => void;
}

type EditorMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; group: CameraGroup };

interface FormState {
  name: string;
  description: string;
}

const NAME_MAX = 100;
const DESC_MAX = 500;
const EMPTY_FORM: FormState = { name: '', description: '' };

function resolveErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof HttpError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function GroupsManagerDialog({
  open,
  groups,
  groupsLoading,
  onClose,
  onChanged,
}: Props): JSX.Element {
  const [editor, setEditor] = useState<EditorMode>({ kind: 'closed' });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<CameraGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEditor({ kind: 'closed' });
      setForm(EMPTY_FORM);
      setErrors({});
      setSubmitError(null);
      setPendingDelete(null);
      setDeleteError(null);
    }
  }, [open]);

  useEffect(() => {
    if (editor.kind === 'edit') {
      setForm({
        name: editor.group.name,
        description: editor.group.description ?? '',
      });
    } else if (editor.kind === 'create') {
      setForm(EMPTY_FORM);
    }
    setErrors({});
    setSubmitError(null);
  }, [editor]);

  const validate = (): Partial<FormState> => {
    const next: Partial<FormState> = {};
    const name = form.name.trim();
    if (!name) next.name = 'Name is required';
    else if (name.length > NAME_MAX) next.name = `Name must be ≤ ${NAME_MAX} characters`;
    if (form.description.length > DESC_MAX)
      next.description = `Description must be ≤ ${DESC_MAX} characters`;
    return next;
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (editor.kind === 'closed') return;
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() === '' ? null : form.description.trim(),
    };
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (editor.kind === 'create') {
        await cameraGroupsApi.create(payload);
      } else {
        await cameraGroupsApi.update(editor.group.id, payload);
      }
      onChanged();
      setEditor({ kind: 'closed' });
    } catch (err) {
      setSubmitError(resolveErrorMessage(err, 'Failed to save group'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await cameraGroupsApi.remove(pendingDelete.id);
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      setDeleteError(resolveErrorMessage(err, 'Failed to delete group'));
    } finally {
      setDeleting(false);
    }
  };

  const headerCount = groupsLoading
    ? 'Loading…'
    : `${groups.length} group${groups.length === 1 ? '' : 's'}`;

  return (
    <>
      <StandardModal
        open={open}
        title="Manage groups"
        size="md"
        onClose={onClose}
        primaryAction={{ label: 'Done', onClick: onClose }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--bui-text-primary)' }}>
              {headerCount}
            </h3>
            {editor.kind === 'closed' && (
              <PrimaryButton size="sm" onClick={() => setEditor({ kind: 'create' })}>
                + New group
              </PrimaryButton>
            )}
          </div>

          {editor.kind !== 'closed' && (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 p-3"
              style={{
                backgroundColor: 'var(--bui-surface-alt)',
                border: '1px solid var(--bui-divider)',
                borderRadius: 'var(--bui-radius-container)',
              }}
            >
              <FormField label="Name" required error={errors.name}>
                {(id, invalid) => (
                  <FormTextInput
                    id={id}
                    autoFocus
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={NAME_MAX}
                    invalid={invalid}
                    disabled={submitting}
                  />
                )}
              </FormField>
              <FormField label="Description" error={errors.description}>
                {(id, invalid) => (
                  <FormTextArea
                    id={id}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    maxLength={DESC_MAX}
                    invalid={invalid}
                    disabled={submitting}
                  />
                )}
              </FormField>
              {submitError && (
                <p
                  role="alert"
                  className="rounded-md px-3 py-2 text-xs"
                  style={{
                    backgroundColor: 'var(--bui-danger-soft)',
                    color: 'var(--bui-danger)',
                  }}
                >
                  {submitError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <SecondaryButton
                  size="sm"
                  onClick={() => setEditor({ kind: 'closed' })}
                  disabled={submitting}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton size="sm" type="submit" busy={submitting}>
                  {editor.kind === 'create' ? 'Create group' : 'Save changes'}
                </PrimaryButton>
              </div>
            </form>
          )}

          {groups.length === 0 && !groupsLoading ? (
            <p
              className="px-3 py-6 text-center text-xs"
              style={{
                border: '1px dashed var(--bui-border)',
                color: 'var(--bui-text-secondary)',
                borderRadius: 'var(--bui-radius-container)',
              }}
            >
              No groups yet. Click <strong>New group</strong> above to add one.
            </p>
          ) : (
            <ul
              className="flex flex-col overflow-hidden"
              style={{
                border: '1px solid var(--bui-divider)',
                borderRadius: 'var(--bui-radius-container)',
              }}
            >
              {groups.map((group, idx) => (
                <li
                  key={group.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  style={{
                    backgroundColor: 'var(--bui-surface)',
                    borderTop: idx === 0 ? undefined : '1px solid var(--bui-divider)',
                  }}
                >
                  <div className="min-w-0">
                    <p
                      className="truncate text-sm font-medium"
                      title={group.name}
                      style={{ color: 'var(--bui-text-primary)' }}
                    >
                      {group.name}
                    </p>
                    {group.description && (
                      <p
                        className="truncate text-xs"
                        title={group.description}
                        style={{ color: 'var(--bui-text-secondary)' }}
                      >
                        {group.description}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <SecondaryButton size="sm" onClick={() => setEditor({ kind: 'edit', group })}>
                      Edit
                    </SecondaryButton>
                    <SecondaryButton
                      size="sm"
                      onClick={() => setPendingDelete(group)}
                      style={{ color: 'var(--bui-danger)', borderColor: 'var(--bui-border)' }}
                    >
                      Delete
                    </SecondaryButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </StandardModal>

      <StandardConfirm
        open={Boolean(pendingDelete)}
        title="Delete group?"
        message={
          pendingDelete
            ? `Cameras in "${pendingDelete.name}" will be unassigned (no recordings are deleted).`
            : ''
        }
        confirmLabel="Delete"
        busy={deleting}
        errorMessage={deleteError}
        onCancel={() => {
          if (!deleting) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
        onConfirm={handleDelete}
      />
    </>
  );
}
